import {Box, useMantineTheme} from '@mantine/core';
import React, {useMemo} from 'react';

import {
  FlowchartValidationSeverity,
  ProcessFlowchartValidationError,
  getValidationSeverity,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {STATUS_COLORS} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';

interface AggregatedError {
  errorMessage: string;
  severity: FlowchartValidationSeverity;
  count: number;
}

const SEVERITY_ICON: Record<FlowchartValidationSeverity, BenchlingIconName> = {
  error: 'circle-xmark',
  warning: 'exclamation-triangle',
};

function aggregateNodeErrors(nodeErrors: Array<ProcessFlowchartValidationError>): Array<AggregatedError> {
  const counts = new Map<string, AggregatedError>();
  for (const error of nodeErrors) {
    const severity = getValidationSeverity(error);
    const key = `${severity}:${error.errorMessage}`;
    const existing = counts.get(key);
    if (existing != null) {
      existing.count += 1;
    } else {
      counts.set(key, {errorMessage: error.errorMessage, severity, count: 1});
    }
  }
  return Array.from(counts.values());
}

export function NodeErrorStatusTooltipContent({
  nodeErrors,
}: {
  nodeErrors: Array<ProcessFlowchartValidationError>;
}): JSX.Element {
  const theme = useMantineTheme();
  const aggregatedErrors = useMemo(() => aggregateNodeErrors(nodeErrors), [nodeErrors]);
  return (
    <Box>
      {aggregatedErrors.map(({errorMessage, severity, count}, index) => {
        const iconColor = theme.colors[STATUS_COLORS[severity].color][STATUS_COLORS[severity].colorIndex];
        return (
          <Box
            key={`${severity}:${errorMessage}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginBottom: index < aggregatedErrors.length - 1 ? '8px' : 0,
            }}
          >
            <BenchlingIcon icon={SEVERITY_ICON[severity]} style={{color: iconColor, height: 16, width: 16}} />
            {errorMessage}
            {count > 1 && (
              <Box component="span" sx={{fontWeight: 600}}>
                {` x${count}`}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
